import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Brain, ArrowLeft, Loader2, TrendingUp, Users, AlertCircle, Target, BarChart3, MessageSquare, PieChart, Filter, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ScatterChart, Scatter, ZAxis, ComposedChart, Line, Area } from 'recharts';
import { AuthContext } from '../App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Component for showing ALL responses with filters
const AllResponsesSection = ({ simulation }) => {
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCount, setShowCount] = useState(20);

  const allResponses = simulation.all_responses || [];

  // Filter responses based on intent level
  const filteredResponses = allResponses.filter(resp => {
    const matchesFilter = filter === 'all' || resp.category === filter;
    const matchesSearch = searchQuery === '' || 
      resp.response.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resp.demographic.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const displayedResponses = filteredResponses.slice(0, showCount);

  const intentCounts = {
    all: allResponses.length,
    'High Intent': allResponses.filter(r => r.category === 'High Intent').length,
    'Moderate Intent': allResponses.filter(r => r.category === 'Moderate Intent').length,
    'Low Intent': allResponses.filter(r => r.category === 'Low Intent').length,
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          All Consumer Responses ({allResponses.length} total)
        </CardTitle>
        <CardDescription>
          Complete feedback from all AI consumers in this simulation
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              All ({intentCounts.all})
            </Button>
            <Button
              variant={filter === 'High Intent' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('High Intent')}
              className={filter === 'High Intent' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              High Intent ({intentCounts['High Intent']})
            </Button>
            <Button
              variant={filter === 'Moderate Intent' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('Moderate Intent')}
              className={filter === 'Moderate Intent' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
            >
              Moderate Intent ({intentCounts['Moderate Intent']})
            </Button>
            <Button
              variant={filter === 'Low Intent' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('Low Intent')}
              className={filter === 'Low Intent' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              Low Intent ({intentCounts['Low Intent']})
            </Button>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search responses or demographics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Response List */}
        <div className="space-y-4">
          {displayedResponses.map((response, idx) => (
            <div 
              key={idx} 
              className={`p-4 rounded-lg border-l-4 ${
                response.category === 'High Intent' ? 'bg-green-50 border-green-500' :
                response.category === 'Moderate Intent' ? 'bg-yellow-50 border-yellow-500' :
                'bg-red-50 border-red-500'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    response.category === 'High Intent' ? 'bg-green-200 text-green-800' :
                    response.category === 'Moderate Intent' ? 'bg-yellow-200 text-yellow-800' :
                    'bg-red-200 text-red-800'
                  }`}>
                    {response.category}
                  </span>
                  <span className="text-xs text-slate-500">{response.demographic}</span>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-bold ${
                    response.score >= 4 ? 'text-green-600' :
                    response.score >= 3 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {typeof response.score === 'number' ? response.score.toFixed(2) : response.score}
                  </div>
                  <div className="text-xs text-slate-500">out of 5.0</div>
                </div>
              </div>
              <p className="text-slate-700 leading-relaxed text-sm">
                "{response.response}"
              </p>
            </div>
          ))}
        </div>

        {/* Load More Button */}
        {filteredResponses.length > showCount && (
          <div className="mt-6 text-center">
            <Button
              variant="outline"
              onClick={() => setShowCount(prev => prev + 20)}
            >
              Load More ({filteredResponses.length - showCount} remaining)
            </Button>
          </div>
        )}

        {filteredResponses.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            No responses match your filters
          </div>
        )}

        {/* Summary */}
        <div className="mt-6 pt-6 border-t">
          <p className="text-sm text-slate-600">
            Showing {displayedResponses.length} of {filteredResponses.length} filtered responses
            {searchQuery && ` (filtered by "${searchQuery}")`}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const SimulationResults = ({ simulation: propSimulation, concept: propConcept, isDemo = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [simulation, setSimulation] = useState(propSimulation || null);
  const [concept, setConcept] = useState(propConcept || null);
  const [loading, setLoading] = useState(!propSimulation);
  const [selectedDemographic, setSelectedDemographic] = useState('all');

  useEffect(() => {
    if (!propSimulation && id) {
      loadSimulation();
      const interval = setInterval(() => {
        loadSimulation();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [id, propSimulation]);

  const loadSimulation = async () => {
    try {
      const simRes = await axios.get(`${API}/simulations/${id}`, { withCredentials: true });
      setSimulation(simRes.data);

      if (simRes.data.product_concept_id) {
        const conceptRes = await axios.get(`${API}/concepts/${simRes.data.product_concept_id}`, { withCredentials: true });
        setConcept(conceptRes.data);
      }
    } catch (error) {
      console.error('Error loading simulation:', error);
      toast.error('Failed to load simulation results');
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistical metrics
  const statistics = useMemo(() => {
    if (!simulation?.score_distribution) return null;
    
    const dist = simulation.score_distribution;
    const total = Object.values(dist).reduce((sum, count) => sum + count, 0);
    
    // Calculate mean
    const mean = Object.entries(dist).reduce((sum, [score, count]) => {
      return sum + (parseInt(score) * count);
    }, 0) / total;
    
    // Calculate variance and standard deviation
    const variance = Object.entries(dist).reduce((sum, [score, count]) => {
      const diff = parseInt(score) - mean;
      return sum + (diff * diff * count);
    }, 0) / total;
    
    const stdDev = Math.sqrt(variance);
    
    // Calculate confidence interval (95%)
    const standardError = stdDev / Math.sqrt(total);
    const marginOfError = 1.96 * standardError;
    const ciLower = mean - marginOfError;
    const ciUpper = mean + marginOfError;
    
    // Calculate percentiles
    let cumulative = 0;
    let median = 0;
    let p25 = 0;
    let p75 = 0;
    
    for (let score = 1; score <= 5; score++) {
      const count = dist[score.toString()] || 0;
      cumulative += count;
      const percentile = cumulative / total;
      
      if (percentile >= 0.25 && p25 === 0) p25 = score;
      if (percentile >= 0.50 && median === 0) median = score;
      if (percentile >= 0.75 && p75 === 0) p75 = score;
    }
    
    return {
      mean,
      stdDev,
      variance,
      ciLower,
      ciUpper,
      median,
      p25,
      p75,
      total
    };
  }, [simulation]);

  // Prepare crosstab data (age × income heatmap)
  const crosstabData = useMemo(() => {
    if (!simulation?.demographic_breakdown) return null;
    
    const { by_age, by_income } = simulation.demographic_breakdown;
    const ageGroups = Object.keys(by_age || {});
    const incomeGroups = Object.keys(by_income || {});
    
    // Create a matrix showing average scores for each age-income combination
    // In reality, we'd need raw_responses to calculate this accurately
    // For now, we'll create an approximation based on available data
    const matrix = [];
    
    ageGroups.forEach(age => {
      incomeGroups.forEach(income => {
        // Approximate combined score (this is a simplification)
        const ageScore = by_age[age]?.average_score || 0;
        const incomeScore = by_income[income]?.average_score || 0;
        const combinedScore = (ageScore + incomeScore) / 2;
        
        matrix.push({
          age,
          income,
          score: combinedScore,
          label: `${age} × ${income.substring(0, 10)}`
        });
      });
    });
    
    return { matrix, ageGroups, incomeGroups };
  }, [simulation]);

  // Prepare radar chart data for market segments
  const radarData = useMemo(() => {
    if (!simulation?.market_segments) return null;
    
    const segments = simulation.market_segments;
    return [
      { attribute: 'Purchase Intent', 
        'High Intent': segments.high_intent?.percentage || 0,
        'Moderate Intent': segments.moderate_intent?.percentage || 0,
        'Low Intent': segments.low_intent?.percentage || 0
      }
    ];
  }, [simulation]);

  // Distribution trends
  const distributionTrend = useMemo(() => {
    if (!simulation?.score_distribution) return [];
    
    const dist = simulation.score_distribution;
    return Object.entries(dist).map(([score, count]) => ({
      score: parseInt(score),
      count,
      percentage: (count / simulation.num_simulations * 100).toFixed(1),
      label: `Score ${score}`
    }));
  }, [simulation]);

  const getIntentColor = (score) => {
    if (score >= 4) return 'text-green-600';
    if (score >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getIntentLabel = (score) => {
    if (score >= 4.5) return 'Very High';
    if (score >= 4) return 'High';
    if (score >= 3) return 'Moderate';
    if (score >= 2) return 'Low';
    return 'Very Low';
  };

  const getBarColor = (score) => {
    if (score === 5) return '#10b981';
    if (score === 4) return '#84cc16';
    if (score === 3) return '#eab308';
    if (score === 2) return '#f97316';
    return '#ef4444';
  };

  const getHeatmapColor = (score) => {
    // Gradient from red (low) to green (high)
    if (score >= 4.5) return '#10b981';
    if (score >= 4) return '#84cc16';
    if (score >= 3.5) return '#fbbf24';
    if (score >= 3) return '#f59e0b';
    if (score >= 2) return '#f97316';
    return '#ef4444';
  };

  const prepareDistributionData = () => {
    if (!simulation?.score_distribution) return [];
    return [
      { score: '1 (Low)', value: simulation.score_distribution['1'] || 0, numScore: 1 },
      { score: '2', value: simulation.score_distribution['2'] || 0, numScore: 2 },
      { score: '3', value: simulation.score_distribution['3'] || 0, numScore: 3 },
      { score: '4', value: simulation.score_distribution['4'] || 0, numScore: 4 },
      { score: '5 (High)', value: simulation.score_distribution['5'] || 0, numScore: 5 }
    ];
  };

  const exportResults = () => {
    if (!simulation) return;
    
    const data = {
      concept: concept?.name,
      simulation_id: simulation.id,
      purchase_intent_score: simulation.purchase_intent_score,
      statistics,
      demographic_breakdown: simulation.demographic_breakdown,
      emotional_drivers: simulation.emotional_drivers,
      objections: simulation.objections,
      market_segments: simulation.market_segments
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `simulation-${simulation.id}-results.json`;
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      <nav className="border-b border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Brain className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-cyan-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              IntentAI
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={exportResults}>
              <Download className="w-4 h-4 mr-2" />
              Export Results
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header Section */}
        {concept && (
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {concept.name}
            </h1>
            <p className="text-slate-600 mb-4">{concept.description}</p>
            {simulation?.status === 'running' && (
              <div className="flex items-center gap-2 text-indigo-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Simulation in progress...</span>
              </div>
            )}
          </div>
        )}

        {simulation?.status === 'completed' && (
          <>
            {/* Key Metrics Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Purchase Intent Score</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-indigo-600">
                    {simulation.purchase_intent_score?.toFixed(2)}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {getIntentLabel(simulation.purchase_intent_score)}
                  </p>
                  {statistics && (
                    <p className="text-xs text-slate-500 mt-2">
                      95% CI: [{statistics.ciLower.toFixed(2)}, {statistics.ciUpper.toFixed(2)}]
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Standard Deviation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-slate-700">
                    {statistics?.stdDev.toFixed(2)}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Variance: {statistics?.variance.toFixed(2)}
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    Median: {statistics?.median}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">Sample Size</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-cyan-600">
                    {simulation.num_simulations}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    AI consumers tested
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    IQR: {statistics?.p25} - {statistics?.p75}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600">High Intent %</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold text-green-600">
                    {simulation.score_distribution ? (
                      ((simulation.score_distribution['4'] + simulation.score_distribution['5']) / simulation.num_simulations * 100).toFixed(1)
                    ) : 0}%
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Scores 4-5
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    Low Intent: {simulation.score_distribution ? (
                      ((simulation.score_distribution['1'] + simulation.score_distribution['2']) / simulation.num_simulations * 100).toFixed(1)
                    ) : 0}%
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Analytics Tabs */}
            <Tabs defaultValue="distribution" className="space-y-6">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="distribution">Distribution</TabsTrigger>
                <TabsTrigger value="demographics">Demographics</TabsTrigger>
                <TabsTrigger value="crosstab">Crosstab Analysis</TabsTrigger>
                <TabsTrigger value="insights">Insights</TabsTrigger>
                <TabsTrigger value="segments">Segments</TabsTrigger>
                <TabsTrigger value="responses">Responses</TabsTrigger>
              </TabsList>

              {/* Distribution Tab */}
              <TabsContent value="distribution" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5" />
                        Score Distribution
                      </CardTitle>
                      <CardDescription>How consumers rated your concept (1-5 scale)</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={prepareDistributionData()}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="score" />
                          <YAxis />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-white p-3 border border-slate-200 rounded shadow-lg">
                                    <p className="font-semibold">{data.score}</p>
                                    <p className="text-indigo-600">Count: {data.value}</p>
                                    <p className="text-slate-600">
                                      Percentage: {((data.value / simulation.num_simulations) * 100).toFixed(1)}%
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="value">
                            {prepareDistributionData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={getBarColor(entry.numScore)} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Statistical Summary</CardTitle>
                      <CardDescription>Key statistical measures of the distribution</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center p-3 bg-indigo-50 rounded">
                          <span className="text-slate-700 font-medium">Mean (Average)</span>
                          <span className="text-indigo-600 font-bold text-lg">{statistics?.mean.toFixed(3)}</span>
                        </div>
                        
                        <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
                          <span className="text-slate-700 font-medium">Median</span>
                          <span className="text-slate-600 font-bold text-lg">{statistics?.median}</span>
                        </div>
                        
                        <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
                          <span className="text-slate-700 font-medium">Standard Deviation</span>
                          <span className="text-slate-600 font-bold text-lg">{statistics?.stdDev.toFixed(3)}</span>
                        </div>
                        
                        <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
                          <span className="text-slate-700 font-medium">Variance</span>
                          <span className="text-slate-600 font-bold text-lg">{statistics?.variance.toFixed(3)}</span>
                        </div>
                        
                        <div className="p-3 bg-green-50 rounded">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-slate-700 font-medium">95% Confidence Interval</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-green-600 font-bold">{statistics?.ciLower.toFixed(3)}</span>
                            <span className="text-slate-400">to</span>
                            <span className="text-green-600 font-bold">{statistics?.ciUpper.toFixed(3)}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            We are 95% confident the true population mean falls within this range
                          </p>
                        </div>
                        
                        <div className="flex justify-between items-center p-3 bg-slate-50 rounded">
                          <span className="text-slate-700 font-medium">Interquartile Range (IQR)</span>
                          <span className="text-slate-600 font-bold text-lg">{statistics?.p25} - {statistics?.p75}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Distribution Breakdown</CardTitle>
                    <CardDescription>Detailed percentage breakdown by score</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {distributionTrend.map(item => (
                        <div key={item.score} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">Score {item.score}</span>
                            <span className="text-slate-600">{item.count} responses ({item.percentage}%)</span>
                          </div>
                          <Progress 
                            value={parseFloat(item.percentage)} 
                            className="h-2"
                            style={{ 
                              backgroundColor: '#e2e8f0',
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Demographics Tab */}
              <TabsContent value="demographics" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Intent by Age Group</CardTitle>
                      <CardDescription>Average purchase intent across age demographics</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart 
                          data={Object.entries(simulation.demographic_breakdown?.by_age || {}).map(([age, data]) => ({
                            age,
                            score: data.average_score,
                            highIntentPct: data.high_intent_pct
                          }))}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 5]} />
                          <YAxis dataKey="age" type="category" width={80} />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                return (
                                  <div className="bg-white p-3 border border-slate-200 rounded shadow-lg">
                                    <p className="font-semibold">{payload[0].payload.age}</p>
                                    <p className="text-indigo-600">Avg Score: {payload[0].value.toFixed(2)}</p>
                                    <p className="text-green-600">High Intent: {payload[0].payload.highIntentPct}%</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="score" fill="#6366f1" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Intent by Income Level</CardTitle>
                      <CardDescription>Purchase intent across income brackets</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart 
                          data={Object.entries(simulation.demographic_breakdown?.by_income || {}).map(([income, data]) => ({
                            income: income.length > 15 ? income.substring(0, 15) + '...' : income,
                            fullIncome: income,
                            score: data.average_score,
                            highIntentPct: data.high_intent_pct
                          }))}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 5]} />
                          <YAxis dataKey="income" type="category" width={120} />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                return (
                                  <div className="bg-white p-3 border border-slate-200 rounded shadow-lg">
                                    <p className="font-semibold">{payload[0].payload.fullIncome}</p>
                                    <p className="text-indigo-600">Avg Score: {payload[0].value.toFixed(2)}</p>
                                    <p className="text-green-600">High Intent: {payload[0].payload.highIntentPct}%</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="score" fill="#8b5cf6" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Intent by Gender</CardTitle>
                      <CardDescription>Gender-based purchase intent comparison</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <ComposedChart 
                          data={Object.entries(simulation.demographic_breakdown?.by_gender || {}).map(([gender, data]) => ({
                            gender,
                            avgScore: data.average_score,
                            highIntentPct: data.high_intent_pct,
                            count: data.count
                          }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="gender" />
                          <YAxis yAxisId="left" domain={[0, 5]} label={{ value: 'Avg Score', angle: -90, position: 'insideLeft' }} />
                          <YAxis yAxisId="right" orientation="right" domain={[0, 100]} label={{ value: 'High Intent %', angle: 90, position: 'insideRight' }} />
                          <Tooltip />
                          <Legend />
                          <Bar yAxisId="left" dataKey="avgScore" fill="#6366f1" name="Average Score" />
                          <Line yAxisId="right" type="monotone" dataKey="highIntentPct" stroke="#10b981" strokeWidth={2} name="High Intent %" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Intent by Location</CardTitle>
                      <CardDescription>Regional purchase intent patterns</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart 
                          data={Object.entries(simulation.demographic_breakdown?.by_location || {}).map(([location, data]) => ({
                            location,
                            score: data.average_score,
                            highIntentPct: data.high_intent_pct
                          }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="location" />
                          <YAxis domain={[0, 5]} />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                return (
                                  <div className="bg-white p-3 border border-slate-200 rounded shadow-lg">
                                    <p className="font-semibold">{payload[0].payload.location}</p>
                                    <p className="text-cyan-600">Avg Score: {payload[0].value.toFixed(2)}</p>
                                    <p className="text-green-600">High Intent: {payload[0].payload.highIntentPct}%</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="score" fill="#06b6d4" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* Detailed breakdown tables */}
                <Card>
                  <CardHeader>
                    <CardTitle>Complete Demographic Breakdown</CardTitle>
                    <CardDescription>Detailed metrics for each demographic segment</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Age breakdown table */}
                      <div>
                        <h4 className="font-semibold mb-3 text-slate-700">By Age Group</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-100">
                              <tr>
                                <th className="p-2 text-left">Age Group</th>
                                <th className="p-2 text-right">Avg Score</th>
                                <th className="p-2 text-right">Count</th>
                                <th className="p-2 text-right">High Intent %</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(simulation.demographic_breakdown?.by_age || {})
                                .sort(([, a], [, b]) => b.average_score - a.average_score)
                                .map(([age, data]) => (
                                <tr key={age} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="p-2 font-medium">{age}</td>
                                  <td className="p-2 text-right">
                                    <span className={`font-semibold ${getIntentColor(data.average_score)}`}>
                                      {data.average_score.toFixed(2)}
                                    </span>
                                  </td>
                                  <td className="p-2 text-right text-slate-600">{data.count}</td>
                                  <td className="p-2 text-right">
                                    <span className="text-green-600 font-medium">{data.high_intent_pct}%</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Income breakdown table */}
                      <div>
                        <h4 className="font-semibold mb-3 text-slate-700">By Income Level</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-100">
                              <tr>
                                <th className="p-2 text-left">Income Level</th>
                                <th className="p-2 text-right">Avg Score</th>
                                <th className="p-2 text-right">Count</th>
                                <th className="p-2 text-right">High Intent %</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(simulation.demographic_breakdown?.by_income || {})
                                .sort(([, a], [, b]) => b.average_score - a.average_score)
                                .map(([income, data]) => (
                                <tr key={income} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="p-2 font-medium">{income}</td>
                                  <td className="p-2 text-right">
                                    <span className={`font-semibold ${getIntentColor(data.average_score)}`}>
                                      {data.average_score.toFixed(2)}
                                    </span>
                                  </td>
                                  <td className="p-2 text-right text-slate-600">{data.count}</td>
                                  <td className="p-2 text-right">
                                    <span className="text-green-600 font-medium">{data.high_intent_pct}%</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Crosstab Analysis Tab */}
              <TabsContent value="crosstab" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Age × Income Crosstab Analysis</CardTitle>
                    <CardDescription>
                      Heatmap showing purchase intent scores for different age-income combinations
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {crosstabData && (
                      <div className="space-y-4">
                        <div className="grid gap-2" style={{ 
                          gridTemplateColumns: `120px repeat(${crosstabData.incomeGroups.length}, 1fr)` 
                        }}>
                          {/* Header row */}
                          <div className="font-medium text-sm"></div>
                          {crosstabData.incomeGroups.map(income => (
                            <div key={income} className="font-medium text-xs text-center p-2">
                              {income.length > 10 ? income.substring(0, 10) + '...' : income}
                            </div>
                          ))}
                          
                          {/* Data rows */}
                          {crosstabData.ageGroups.map(age => (
                            <React.Fragment key={age}>
                              <div className="font-medium text-sm p-2">{age}</div>
                              {crosstabData.incomeGroups.map(income => {
                                const cell = crosstabData.matrix.find(
                                  c => c.age === age && c.income === income
                                );
                                const score = cell?.score || 0;
                                return (
                                  <div 
                                    key={`${age}-${income}`}
                                    className="p-2 rounded text-center font-semibold text-white text-sm"
                                    style={{ backgroundColor: getHeatmapColor(score) }}
                                    title={`${age} × ${income}: ${score.toFixed(2)}`}
                                  >
                                    {score.toFixed(1)}
                                  </div>
                                );
                              })}
                            </React.Fragment>
                          ))}
                        </div>
                        
                        <div className="flex items-center justify-center gap-4 mt-6 text-xs">
                          <span>Low Intent</span>
                          <div className="flex gap-1">
                            <div className="w-8 h-4 rounded" style={{ backgroundColor: '#ef4444' }}></div>
                            <div className="w-8 h-4 rounded" style={{ backgroundColor: '#f97316' }}></div>
                            <div className="w-8 h-4 rounded" style={{ backgroundColor: '#f59e0b' }}></div>
                            <div className="w-8 h-4 rounded" style={{ backgroundColor: '#fbbf24' }}></div>
                            <div className="w-8 h-4 rounded" style={{ backgroundColor: '#84cc16' }}></div>
                            <div className="w-8 h-4 rounded" style={{ backgroundColor: '#10b981' }}></div>
                          </div>
                          <span>High Intent</span>
                        </div>
                        
                        <p className="text-xs text-slate-600 mt-4">
                          <strong>Note:</strong> This heatmap shows approximated scores based on available demographic data. 
                          Darker green indicates higher purchase intent, while red indicates lower intent.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Key Demographic Correlations</CardTitle>
                    <CardDescription>Insights from demographic patterns</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {simulation.demographic_breakdown && (
                        <>
                          <div className="p-4 bg-green-50 border border-green-200 rounded">
                            <h4 className="font-semibold text-green-800 mb-2">Highest Intent Segment</h4>
                            <p className="text-sm text-slate-700">
                              {(() => {
                                const byAge = simulation.demographic_breakdown.by_age || {};
                                const topAge = Object.entries(byAge).sort(([,a], [,b]) => b.average_score - a.average_score)[0];
                                const byIncome = simulation.demographic_breakdown.by_income || {};
                                const topIncome = Object.entries(byIncome).sort(([,a], [,b]) => b.average_score - a.average_score)[0];
                                return `${topAge?.[0]} age group (${topAge?.[1].average_score.toFixed(2)}) and ${topIncome?.[0]} income level (${topIncome?.[1].average_score.toFixed(2)}) show the strongest purchase intent.`;
                              })()}
                            </p>
                          </div>
                          
                          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                            <h4 className="font-semibold text-yellow-800 mb-2">Moderate Intent Segment</h4>
                            <p className="text-sm text-slate-700">
                              Middle-income brackets and mid-age groups show moderate interest, indicating potential with refined messaging.
                            </p>
                          </div>
                          
                          <div className="p-4 bg-red-50 border border-red-200 rounded">
                            <h4 className="font-semibold text-red-800 mb-2">Lowest Intent Segment</h4>
                            <p className="text-sm text-slate-700">
                              {(() => {
                                const byAge = simulation.demographic_breakdown.by_age || {};
                                const lowAge = Object.entries(byAge).sort(([,a], [,b]) => a.average_score - b.average_score)[0];
                                const byIncome = simulation.demographic_breakdown.by_income || {};
                                const lowIncome = Object.entries(byIncome).sort(([,a], [,b]) => a.average_score - b.average_score)[0];
                                return `${lowAge?.[0]} age group (${lowAge?.[1].average_score.toFixed(2)}) and ${lowIncome?.[0]} income level (${lowIncome?.[1].average_score.toFixed(2)}) show the lowest purchase intent.`;
                              })()}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Insights Tab */}
              <TabsContent value="insights" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-green-600">
                        <TrendingUp className="w-5 h-5" />
                        Key Emotional Drivers
                      </CardTitle>
                      <CardDescription>What motivates consumers to purchase</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {simulation.emotional_drivers?.map((driver, idx) => (
                          <li key={idx} className="flex items-start gap-3 p-3 bg-green-50 rounded">
                            <div className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">
                              {idx + 1}
                            </div>
                            <span className="text-slate-700 text-sm leading-relaxed">{driver}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-red-600">
                        <AlertCircle className="w-5 h-5" />
                        Top Objections & Concerns
                      </CardTitle>
                      <CardDescription>Barriers preventing purchase</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {simulation.objections?.map((objection, idx) => (
                          <li key={idx} className="flex items-start gap-3 p-3 bg-red-50 rounded">
                            <div className="w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">
                              {idx + 1}
                            </div>
                            <span className="text-slate-700 text-sm leading-relaxed">{objection}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                {simulation.demographic_insights && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-indigo-600" />
                        Strategic Recommendations
                      </CardTitle>
                      <CardDescription>Data-driven insights for optimization</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-2">Target Segment</h4>
                          <p className="text-sm text-slate-600 bg-indigo-50 p-3 rounded">
                            {simulation.demographic_insights.top_segment}
                          </p>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-2">Estimated Conversion Rate</h4>
                          <p className="text-sm text-slate-600 bg-cyan-50 p-3 rounded">
                            {simulation.demographic_insights.conversion_rate_estimate}
                          </p>
                        </div>
                        
                        {simulation.demographic_insights.best_channels && (
                          <div>
                            <h4 className="font-semibold text-slate-700 mb-2">Recommended Channels</h4>
                            <div className="flex flex-wrap gap-2">
                              {simulation.demographic_insights.best_channels.map((channel, idx) => (
                                <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                                  {channel}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {simulation.demographic_insights.messaging_recommendations && (
                          <div>
                            <h4 className="font-semibold text-slate-700 mb-3">Messaging Recommendations</h4>
                            <ul className="space-y-2">
                              {simulation.demographic_insights.messaging_recommendations.map((rec, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                                  <span className="text-indigo-600 font-bold">•</span>
                                  <span>{rec}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* NEW: Power Segments */}
                {simulation.power_segments && simulation.power_segments.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-purple-600" />
                        🎯 Your Top Customer Segments
                      </CardTitle>
                      <CardDescription>Highest-intent demographic combinations</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {simulation.power_segments.map((seg, idx) => (
                          <div key={idx} className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold">
                                {idx + 1}
                              </div>
                              <div>
                                <p className="font-semibold text-slate-800">{seg.segment}</p>
                                <p className="text-sm text-slate-600">{seg.count} consumers tested</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-purple-600">{seg.avg_score}/5.0</p>
                              <p className="text-sm text-slate-600">{seg.high_intent_pct}% high intent</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* NEW: Objections by Segment */}
                {simulation.objections_by_segment && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Filter className="w-5 h-5 text-orange-600" />
                        📊 Concerns by Intent Level
                      </CardTitle>
                      <CardDescription>Different objections for interested vs uninterested consumers</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            High Intent Concerns
                          </h4>
                          <div className="space-y-2">
                            {simulation.objections_by_segment.high_intent_concerns?.length > 0 ? (
                              simulation.objections_by_segment.high_intent_concerns.map((concern, idx) => (
                                <div key={idx} className="px-3 py-2 bg-green-50 text-green-800 rounded text-sm border border-green-200">
                                  {concern}
                                </div>
                              ))
                            ) : (
                              <p className="text-sm text-slate-500 italic">No major concerns from high-intent consumers</p>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-2">What stops interested buyers from purchasing</p>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            Low Intent Concerns
                          </h4>
                          <div className="space-y-2">
                            {simulation.objections_by_segment.low_intent_concerns?.length > 0 ? (
                              simulation.objections_by_segment.low_intent_concerns.map((concern, idx) => (
                                <div key={idx} className="px-3 py-2 bg-red-50 text-red-800 rounded text-sm border border-red-200">
                                  {concern}
                                </div>
                              ))
                            ) : (
                              <p className="text-sm text-slate-500 italic">Not enough low-intent responses</p>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-2">Why people aren't interested</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* NEW: Price Sensitivity */}
                {simulation.price_sensitivity && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-emerald-600" />
                        💰 Price Perception Analysis
                      </CardTitle>
                      <CardDescription>How consumers view your pricing at {simulation.price_sensitivity.price_point}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                          <p className="text-sm text-slate-600 mb-1">Too Expensive</p>
                          <p className="text-3xl font-bold text-red-600">{simulation.price_sensitivity.too_expensive_pct}%</p>
                        </div>
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm text-slate-600 mb-1">Good Value</p>
                          <p className="text-3xl font-bold text-green-600">{simulation.price_sensitivity.good_value_pct}%</p>
                        </div>
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm text-slate-600 mb-1">Would Pay More</p>
                          <p className="text-3xl font-bold text-blue-600">{simulation.price_sensitivity.would_pay_more_pct}%</p>
                        </div>
                      </div>
                      <p className="text-sm text-slate-500 mt-4">
                        {simulation.price_sensitivity.price_mentioned_pct}% of consumers mentioned pricing in their feedback
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Market Segments Tab */}
              <TabsContent value="segments" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {simulation.market_segments && Object.entries(simulation.market_segments).map(([key, segment]) => (
                    <Card key={key} className={
                      key === 'high_intent' ? 'border-2 border-green-300' :
                      key === 'moderate_intent' ? 'border-2 border-yellow-300' :
                      'border-2 border-red-300'
                    }>
                      <CardHeader>
                        <CardTitle className={
                          key === 'high_intent' ? 'text-green-600' :
                          key === 'moderate_intent' ? 'text-yellow-600' :
                          'text-red-600'
                        }>
                          {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </CardTitle>
                        <CardDescription>
                          <span className="text-2xl font-bold">{segment.percentage}%</span> of consumers
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <h4 className="font-semibold text-sm mb-2 text-slate-700">Characteristics:</h4>
                        <ul className="space-y-2">
                          {segment.characteristics?.map((char, idx) => (
                            <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                              <span className="text-slate-400 mt-1">•</span>
                              <span>{char}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {radarData && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Segment Comparison</CardTitle>
                      <CardDescription>Visual comparison of market segment sizes</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart 
                          data={Object.entries(simulation.market_segments).map(([key, segment]) => ({
                            segment: key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
                            percentage: segment.percentage
                          }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="segment" />
                          <YAxis domain={[0, 100]} label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }} />
                          <Tooltip />
                          <Bar dataKey="percentage">
                            <Cell fill="#10b981" />
                            <Cell fill="#fbbf24" />
                            <Cell fill="#ef4444" />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Sample Responses Tab */}
              <TabsContent value="responses" className="space-y-6">
                <AllResponsesSection simulation={simulation} />
              </TabsContent>
            </Tabs>
          </>
        )}

        {simulation?.status === 'running' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                <div>
                  <h3 className="font-semibold text-slate-800">Simulation in Progress</h3>
                  <p className="text-sm text-slate-600">
                    Running {simulation.num_simulations} AI consumer simulations. This may take a few minutes...
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {simulation?.status === 'failed' && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 text-red-600">
                <AlertCircle className="w-6 h-6" />
                <div>
                  <h3 className="font-semibold">Simulation Failed</h3>
                  <p className="text-sm">Please try running the simulation again.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SimulationResults;
